// @ts-check
import { DelayTimer, KeyboardEventKeyCode, Themes } from "consts";
import React, { useEffect } from "react";
import { isTheme, onFocusRef } from "utils";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/modal/Modal.scss';

/**
 * @typedef {import('preact').JSX.Element} JSXElement
 * @typedef {import('preact').ComponentChild} ComponentChild
 * @typedef {import('preact').RefObject<HTMLElement>} RefObject
 */

export const ModalDirection = {
  Top: "modal-top",
  Bottom: "modal-bottom",
  Left: "modal-left",
  Right: "modal-right",
};

/**
 * @typedef {Object} ModalProps
 * @property {ComponentChild | ComponentChild[]} children - Modal content
 * @property {string} id - Unique identifier for the modal
 * @property {string} [className] - Optional CSS class name
 * @property {string} [direction] - Modal slide direction (use ModalDirection values)
 * @property {string} [theme] - Theme name for styling
 * @property {boolean} [showModal] - Whether modal is visible
 * @property {() => void} [onClose] - Callback when modal closes
 * @property {RefObject} [openFocusRef] - Ref to focus when modal opens
 * @property {RefObject} [closeFocusRef] - Ref to focus when modal closes
 * @property {string} [ariaLabeledById] - ID of element that labels this modal
 * @property {boolean} [focusable] - Whether modal should manage focus
 * @property {boolean} [escapable] - Whether ESC key closes modal
 */

/**
 * Modal component that displays content in an overlay
 * @param {ModalProps} props
 * @returns {(JSXElement | null)[] | null}
 */
const Modal = ({
  children,
  className,
  direction,
  theme,
  showModal,
  onClose,
  openFocusRef,
  closeFocusRef,
  ariaLabeledById,
  id,
  focusable = false,
  escapable = true,
}) => {
  if (!children) {
    return null;
  }
  const directionClass = direction ? direction : ModalDirection.Bottom;
  const themeClass = isTheme(theme) ? `theme-${theme}` : `theme-${Themes.Dark}`;
  const showClass = showModal ? "show-modal" : "";
  const modalClassName = `csw-modal ${themeClass} ${directionClass} ${showClass} ${className ? className : ""}`;
  const modalGlassClassName = `csw-modal-glass ${showModal ? "show-glass" : ""} ${onClose ? "glass-closable" : ""}`;

  const onModalClose = onClose
    ? onClose
    : () => {
        //Do nothing
      };

  // useEffect for showModal, focusable
  useEffect(() => {
    showModal && focusable && setTimeout(setMenuFocus, DelayTimer.WidgetMenuFocusDelay);
  }, [showModal, focusable]);

  const setMenuFocus = () => {
    const focusRef = showModal ? openFocusRef : closeFocusRef;

    return focusable && onFocusRef(focusRef);
  };

  /** @param {KeyboardEvent} e */
  const escapeKeyHandler = (e) => escapable && e && e.keyCode === KeyboardEventKeyCode.Escape && onModalClose();

  return [
    showModal ? (
      <div className={modalGlassClassName} key="modal-glass" onClick={onModalClose}>
        {" "}
      </div>
    ) : null,
    <div
      className={modalClassName}
      role="dialog"
      key="modal-container"
      id={id}
      aria-hidden={!showModal}
      aria-labelledby={ariaLabeledById}
      onKeyUp={escapeKeyHandler}
    >
      <div className="csw-modal-content">{children}</div>
    </div>,
  ];
};

export default Modal;
